/**
 * setupRedirectReturn.ts — the patient coming BACK from their bank's 3D Secure
 * page, and the return address that lets the checkout be found again.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: checkout, 3D Secure
 * redirect return (prod defect, 2026-09-25).
 *
 * THE DEFECT. `PaymentElementsForm` confirms the card with
 * `stripe.confirmSetup({ redirect: 'if_required', confirmParams: { return_url } })`.
 * Most 3D Secure checks run in a modal and the promise answers in place. Some
 * banks need a full-page REDIRECT: Stripe navigates the tab to the bank, and the
 * bank sends the patient back to `return_url` with
 * `?setup_intent=seti_…&setup_intent_client_secret=…&redirect_status=succeeded`
 * appended. The promise that would have called the attach never answers — its
 * document is gone. Nothing read those parameters, so the attach
 * (`POST …/payment-method`) never ran: no card saved, no month-1 hold, the case
 * stayed `awaiting_payment`, and the lead gate never opened. Two guests on
 * 2026-09-14 and 2026-09-19 came back with `redirect_status=succeeded` and were
 * lost.
 *
 * AND THE CHECKOUT COULD NOT BE FOUND AGAIN, ON EVERY LANE. The return is a FULL
 * page load, so nothing in memory survives it. `return_url` was
 * `window.location.href`, which on the in-flow lane is
 * `/weightloss?assessment=…&checkout=1` — a cold `?assessment=` restore that asks
 * a guest for the emailed 6-digit code before it will show them anything (the
 * Option C gate) — and on the plan-link and dashboard lanes is a bare
 * `/weightloss`, because `#resume=` is scrubbed on arrival: a new Step 0.
 *
 * THE DECISION: THE RETURN ADDRESS CARRIES THE ASSESSMENT, IN THE FRAGMENT.
 * `checkoutReturnUrl` builds `return_url` as the page's own origin, path and
 * query plus `#checkout-return=<assessmentId>`. That is the estate's rule for an
 * id that re-enters checkout (`#resume=`, `lib/orderResume.ts`): the FRAGMENT,
 * never a query string, so the id never reaches an access log, the `Referer` of
 * the next hop or GTM's `page_location`, and it is scrubbed on arrival. It is not
 * `#resume=` itself, because that key is the clinician's plan link: it fires the
 * plan-viewed beacon and offers the plan-link drop-off question, and a 3D Secure
 * return is neither. No device storage carries the context (sessionStorage is
 * per tab, and a bank app can hand the return to a different tab): the address
 * is the whole of it. A guest's checkout grant is MINTED FRESH on every
 * return, through the same public guest-lane mint as the plan link — no new
 * capability, since that mint is keyed by the assessment id alone. The token the
 * tab stored is deliberately NOT reused (release review, 2026-09-25): on the
 * emailed-code restore lane it is clinic's 15-minute re-entry token, and a bank
 * step that outlasts it came back to a refused resume with nothing left to
 * retry the return from.
 *
 * A CLINICIAN-BUILT PLAN (returning patients S7.2, 2026-09-25 — this was the
 * release review's KNOWN GAP). The callback lane's plan link ("Your clinician
 * has built your plan", charged on collection) keeps its order case in
 * `preapproved` until the attach, and orders' `checkout/resume` used to restore
 * `awaiting_payment`, `plan_selected` and the post-payment states only, so the
 * return came back to `none` and was set aside on the plan screen: card saved at
 * Stripe, no money taken, the patient made to re-enter delivery and press again.
 * orders S7.2a (`fix/resume-clinician-plan`) now answers `clinician_plan` for a
 * `preapproved` case BOUND to the caller, with its SetupIntent and address and
 * nothing held, re-issuing the case's own session under the same idempotency
 * key; CheckoutFlow (S7.2b) restores it to the card step with `clinicianPlan`
 * set, and the return is finished exactly as on the hold route, except that the
 * attach captures. Two lanes are still set aside, deliberately:
 *  - A GUEST. orders answers a clinician plan only to the principal the case is
 *    bound to, and a guest principal never binds one (S0.4 refuses it outright;
 *    before S0.4 the gate needed an identity email the guest id does not have),
 *    so a guest's resume is `none`. (The self-pick chooser a signed-out guest
 *    then lands on is S0.4's pre-existing landing, not a designed outcome: see
 *    CLAUDE.md, "WHAT A SIGNED-OUT PATIENT THEN SEES IS NOT DESIGNED".)
 *  - AN ORDERS BUILD WITHOUT S7.2a, which answers `none` for the same case — the
 *    portal keeps the behaviour it had before the status existed.
 * Both pinned in `redirectReturn.spec.tsx`.
 *
 * What the id reveals, stated so nobody over-claims the fragment: the id also
 * travels to Stripe INSIDE `return_url` (it is a parameter of the confirm call
 * and is stored on the SetupIntent). The in-flow lane already sent it there in
 * its `?assessment=` query; it is an opaque UUID, with no name, email or health
 * fact in it. And Stripe's own three parameters ride the QUERY of the return
 * request, by Stripe's design — so the client secret reaches the load balancer's
 * access log whatever this file does. The scrub stops it going any further.
 *
 * THE SCRUB (`stripCheckoutReturnKeys`, run by `journey/handoffScrub.ts` in the
 * same single `replace` that takes `#resume=` and `#lead=` off the address bar,
 * on the journey's FIRST commit — before AppShell loads the GTM container). A
 * reload, a Back/Forward or a shared link therefore never replays the return:
 * the attach and its two funnel events can only come from the document the bank
 * sent the patient to. The journey reads what it needs (`readCheckoutRedirectReturn`)
 * in lazy state BEFORE that scrub runs.
 *
 * PARSED DEFENSIVELY. Stripe appends its parameters to the query of `return_url`
 * and keeps the fragment. Should any hop ever append them AFTER the `#`, the
 * readers here still find them (a `?` inside the fragment is read as `&`), because
 * the cost of a missed read is this defect again.
 */
import type { Location } from 'react-router-dom';
import { isAssessmentIdShaped } from '../journey/resumeHandoff';
import { stripKeyFromLocation } from '../journey/urlKeys';

/** The fragment key that names the checkout on a 3D Secure return address. */
export const CHECKOUT_RETURN_KEY = 'checkout-return';

/** Stripe's three return parameters (Stripe.js `confirmSetup`, redirect flow). */
const STRIPE_SETUP_RETURN_KEYS = ['setup_intent', 'setup_intent_client_secret', 'redirect_status'] as const;

/** Every key this module owns on the address bar — what the scrub removes. */
const RETURN_KEYS: ReadonlyArray<string> = [CHECKOUT_RETURN_KEY, ...STRIPE_SETUP_RETURN_KEYS];

/**
 * A SetupIntent id's SHAPE (`seti_` + a token). A shape check, not a validity
 * check — Stripe answers for the id itself when it is retrieved, and orders on
 * the attach. Underscores are allowed so the test doubles' ids pass too.
 */
const SETUP_INTENT_ID = /^seti_[A-Za-z0-9_]{1,255}$/;
/** A redirect status is a Stripe enum word — nothing else is read out of the URL. */
const REDIRECT_STATUS = /^[a-z_]{1,40}$/;

/**
 * The bank's answer, as Stripe's return parameters state it. A CLAIM from the
 * URL, never proof: `redirect_status=succeeded` is verified with
 * `stripe.retrieveSetupIntent(clientSecret)` before anything is attached, and
 * orders re-checks the SetupIntent server-side on the attach (R41).
 */
export interface SetupRedirectResult {
  setupIntentId: string;
  /**
   * The SetupIntent's client secret, or null when it is absent or does not
   * belong to `setupIntentId` (Stripe encodes the id as the secret's prefix). A
   * success cannot be verified without it, so a null secret is never attached.
   */
  clientSecret: string | null;
  /** `succeeded`, `failed`, … — Stripe's word for how the bank's check ended. */
  redirectStatus: string;
}

/** A 3D Secure return the journey can act on: WHICH checkout, and what the bank said. */
export interface CheckoutRedirectReturn {
  assessmentId: string;
  setup: SetupRedirectResult;
}

/**
 * The fragment's key/value pairs. A `?` inside it is read as a separator, so
 * parameters a hop appended after the `#` are still found (see the header).
 */
function fragmentParams(hash: string): URLSearchParams {
  return new URLSearchParams(hash.replace(/^#/, '').replace('?', '&'));
}

/** One parameter from the query, else from the fragment — trimmed, empty is absent. */
function readParam(location: Pick<Location, 'search' | 'hash'>, key: string): string | null {
  const fromQuery = new URLSearchParams(location.search).get(key)?.trim();
  if (fromQuery) return fromQuery;
  return fragmentParams(location.hash).get(key)?.trim() || null;
}

/** Stripe's half of a return, or null when the URL carries no usable one. */
export function readSetupRedirectResult(location: Pick<Location, 'search' | 'hash'>): SetupRedirectResult | null {
  const setupIntentId = readParam(location, 'setup_intent');
  const redirectStatus = readParam(location, 'redirect_status');
  if (!setupIntentId || !SETUP_INTENT_ID.test(setupIntentId)) return null;
  if (!redirectStatus || !REDIRECT_STATUS.test(redirectStatus)) return null;
  const secret = readParam(location, 'setup_intent_client_secret');
  // The secret must be THIS intent's: Stripe prefixes it with the intent id. A
  // secret for another intent cannot verify this one, so it is dropped.
  const clientSecret = secret && secret.startsWith(`${setupIntentId}_secret_`) ? secret : null;
  return { setupIntentId, clientSecret, redirectStatus };
}

/**
 * A 3D Secure return the journey should re-enter checkout for, or null.
 *
 * WHICH CHECKOUT: the `#checkout-return=` fragment this portal writes into
 * `return_url`, else the page's own `?assessment=` — the in-flow lane's durable
 * identity, and the only one a return from a build that predates the fragment
 * carries. Both must be UUID-shaped (a SHAPE check; orders decides whether the
 * id means anything). No Stripe parameters, or no checkout they belong to →
 * null, and the page is whatever the rest of the URL says it is.
 */
export function readCheckoutRedirectReturn(
  location: Pick<Location, 'search' | 'hash'>,
): CheckoutRedirectReturn | null {
  const setup = readSetupRedirectResult(location);
  if (!setup) return null;
  const fromFragment = fragmentParams(location.hash).get(CHECKOUT_RETURN_KEY)?.trim();
  const fromQuery = new URLSearchParams(location.search).get('assessment')?.trim();
  const assessmentId = [fromFragment, fromQuery].find((id): id is string => Boolean(id && isAssessmentIdShaped(id)));
  return assessmentId ? { assessmentId, setup } : null;
}

/** Does the address bar carry any key this module owns (so the scrub has work to do)? */
export function hasCheckoutReturnKeys(location: Pick<Location, 'search' | 'hash'>): boolean {
  return RETURN_KEYS.some((key) => readParam(location, key) !== null);
}

/**
 * The search + hash with every return key removed — PURE, like the other
 * strippers; the one navigation is `handoffScrub.ts`'s. Every other segment
 * survives byte for byte (`?assessment=`, `?checkout=1`, the attribution tags).
 */
export function stripCheckoutReturnKeys(location: Pick<Location, 'search' | 'hash'>): {
  search: string;
  hash: string;
} {
  return RETURN_KEYS.reduce<{ search: string; hash: string }>(
    (current, key) => stripKeyFromLocation(current, key),
    { search: location.search, hash: location.hash },
  );
}

/**
 * THE `return_url` FOR `confirmSetup`: this page, with its query as it stands
 * (less any stale return keys), and `#checkout-return=<assessmentId>` as the
 * WHOLE fragment. Absolute, because Stripe requires it; built from the real
 * address bar at the moment of the press, so it carries the served prefix
 * (`BASENAME`) and the journey's attribution tags without re-deriving either.
 */
export function checkoutReturnUrl(
  assessmentId: string,
  here: Pick<Location, 'pathname' | 'search'> & { origin: string } = window.location,
): string {
  const { search } = stripCheckoutReturnKeys({ search: here.search, hash: '' });
  return `${here.origin}${here.pathname}${search}#${CHECKOUT_RETURN_KEY}=${encodeURIComponent(assessmentId)}`;
}

/** Stripe's `redirect_status` words that mean the bank's check did NOT set the card up. */
const FAILED_REDIRECT_STATUSES: ReadonlySet<string> = new Set(['failed', 'requires_payment_method']);

/**
 * What a return's claim is worth BEFORE anything is asked of Stripe, for the
 * checkout the page restored (`sessionSetupIntentId`, the SetupIntent orders
 * names for this case now):
 *  - `ignore`: not this order's card (another intent — the case was re-planned,
 *    or the URL is not ours), or a status that says nothing either way. The
 *    screen is the order as orders holds it, and nothing is attached;
 *  - `declined`: the bank's check failed. The card step says the card was
 *    declined; the SetupIntent is back to `requires_payment_method`, so the card
 *    form in front of the patient can take a card again;
 *  - `verify`: the bank said yes. Ask Stripe (`retrieveSetupIntent`) before
 *    attaching — the URL is a claim, the SetupIntent's own status is the fact;
 *  - `unverifiable`: a success with no usable client secret — it cannot be
 *    checked, so it is not attached (a press of Place order still recovers it:
 *    Stripe answers "already succeeded" and the card is re-attached).
 */
export function redirectReturnAction(
  result: SetupRedirectResult,
  sessionSetupIntentId: string,
): 'ignore' | 'declined' | 'verify' | 'unverifiable' {
  if (result.setupIntentId !== sessionSetupIntentId) return 'ignore';
  if (FAILED_REDIRECT_STATUSES.has(result.redirectStatus)) return 'declined';
  if (result.redirectStatus !== 'succeeded') return 'ignore';
  return result.clientSecret ? 'verify' : 'unverifiable';
}

/**
 * What Stripe's own record of the SetupIntent says, once asked
 * (`stripe.retrieveSetupIntent(clientSecret)`). Only `succeeded` on THIS
 * order's intent attaches; the rest pick the card step's sentence.
 *  - `not_this_order`: the secret opened a different intent — never attached;
 *  - `declined` / `authentication`: the card is not set up, and why, in the same
 *    words an in-place confirm would have produced;
 *  - `unconfirmed`: Stripe could not be asked, or answered with a state that is
 *    neither (e.g. `processing`) — nothing is attached and nothing is claimed.
 */
export function redirectSetupVerdict(
  setupIntent: { id: string; status: string } | null | undefined,
  sessionSetupIntentId: string,
): 'attach' | 'not_this_order' | 'declined' | 'authentication' | 'unconfirmed' {
  if (!setupIntent) return 'unconfirmed';
  if (setupIntent.id !== sessionSetupIntentId) return 'not_this_order';
  switch (setupIntent.status) {
    case 'succeeded':
      return 'attach';
    case 'requires_payment_method':
    case 'canceled':
      return 'declined';
    case 'requires_action':
    case 'requires_confirmation':
      return 'authentication';
    default:
      return 'unconfirmed';
  }
}
